import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface HeroSectionProps {
  src: string;
  poster: string;
  headerOffset?: number;
}

const Hero: React.FC<HeroSectionProps> = ({
  src,
  poster,
  headerOffset = 80,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);

  const handleEnded = () => {
    // ⏹ Arrête complètement la vidéo après la première lecture
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowCard(true);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;

      // Stocke la durée si pas déjà connue
      if (duration && !videoDuration) setVideoDuration(duration);

      // ⏳ Affiche la carte 5 sec avant la fin
      if (duration && currentTime >= duration - 5) {
        setShowCard(true);
      }
    }
  };

  return (
    <section
      style={{
        marginTop: headerOffset + 30,
        minHeight: `calc(80vh - ${headerOffset}px)`,
      }}
      className="relative flex items-center justify-center overflow-hidden sm:min-h-[80vh] min-h-[60vh]"
    >
      {/* BACKGROUND : Vidéo ou image */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain sm:object-cover bg-black"
        src={src}
        poster={poster}
        autoPlay
        muted
        playsInline
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      />

      {/* CARTE alignée à gauche */}
      {showCard && (
        <div className="pointer-events-none absolute top-1/2 left-4 sm:left-6 md:left-8 lg:left-12 xl:left-16 -translate-y-1/2 z-10 
                        w-[90vw] max-w-[85vw] sm:max-w-[75vw] md:max-w-[55vw] lg:max-w-[45vw] xl:max-w-[35vw]">
          <div className="pointer-events-auto bg-white/70 backdrop-blur-md text-gray-900 
                          border border-white/50 shadow-2xl rounded-2xl p-4 sm:p-5 md:p-6 lg:p-7
                          transform-gpu min-w-0 max-w-full">
            <div className="flex flex-col items-center sm:items-start text-center sm:text-left space-y-3 sm:space-y-4 md:space-y-5">
              {/* Texte */}
              <div className="w-full">
                <h1 className="text-[clamp(1.25rem,4vw,2.5rem)] sm:text-[clamp(1.5rem,3vw,3rem)] md:text-[clamp(1.75rem,2.5vw,3.5rem)] font-bold leading-tight mb-2 sm:mb-3 md:mb-4">
                  La qualité logicielle, votre avantage compétitif
                </h1>
                <p className="text-[clamp(0.875rem,2.5vw,1rem)] sm:text-[clamp(0.9rem,2vw,1.1rem)] md:text-[clamp(0.95rem,1.5vw,1.2rem)] leading-relaxed max-w-full">
                  Neoqualis est une ESN française experte en Testing (QA) et Business Analysis.
                  Nous fiabilisons vos applications, réduisons les risques et accélérons vos mises
                  en production grâce à l'automatisation des tests, aux tests de performance et à
                  notre plateforme QualisAI. Avec Qualis-Academy, vos équipes adoptent durablement
                  les meilleures pratiques.
                </p>
              </div>

              {/* CTA */}
              <div className="flex flex-col xs:flex-row gap-2 sm:gap-3 w-full">
                <Link
                    to="/expertise"
                    className="btn-primary inline-flex items-center justify-center px-2 py-1.5 xs:px-3 xs:py-2 sm:px-3 sm:py-2 md:px-4 md:py-2.5
                            text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] md:text-[0.95rem] lg:text-[1rem]
                            min-w-0 flex-1 whitespace-nowrap text-center leading-tight"
                >
                    <span className="truncate">Découvrir nos expertises</span>
                    <ArrowRight className="ml-1.5 w-3 h-3 xs:w-3.5 xs:h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                </Link>
                <Link
                    to="/audit"
                    className="btn-secondary inline-flex items-center justify-center px-2 py-1.5 xs:px-3 xs:py-2 sm:px-3 sm:py-2 md:px-4 md:py-2.5
                            text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] md:text-[0.95rem] lg:text-[1rem]
                            min-w-0 flex-1 whitespace-nowrap text-center leading-tight"
                >
                    <span className="truncate">Audit QA gratuit</span>
                    <ArrowRight className="ml-1.5 w-3 h-3 xs:w-3.5 xs:h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                </Link>
                </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;